import css, { get } from '@styled-system/css';
import produce from 'immer';
import { darken, transparentize } from 'polished';
import styled from 'styled-components';
import { alignSelf, border, color, compose, space, typography, variant } from 'styled-system';
import theme from '../theme';
const button = css(theme => ({
    cursor: 'pointer',
    fontFamily: 'body',
    fontSize: '0',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    borderStyle: 'solid',
    borderRadius: '1',
    ':focus': {
        boxShadow: `0 0 1px 1px ${get(theme, 'colors.gray.3', 'lightgray')}`,
        outline: '0'
    }
}));
const sizeVariant = variant({
    prop: 'size',
    scale: 'buttonSizes',
    variants: {
        large: {
            borderWidth: '2px',
            fontSize: '1',
            lineHeight: '1.15',
            padding: '3'
        },
        medium: {
            borderWidth: '1px',
            fontSize: '0',
            lineHeight: '1.25',
            padding: '2'
        },
        small: {
            borderWidth: '1px',
            fontSize: '12px',
            lineHeight: '1.25',
            padding: '1'
        }
    }
});
function modifyColor({ theme, styles, properties, func, amount }) {
    const modifiedStyles = css(styles)(theme);
    for (const key of properties) {
        if (!modifiedStyles[key]) {
            continue;
        }
        amount = get(theme, `colorModifiers.${amount}`, amount);
        modifiedStyles[key] = func(amount, modifiedStyles[key]);
    }
    return modifiedStyles;
}
const darkenHover = (args) => modifyColor(Object.assign(Object.assign({}, args), { func: darken, amount: 'darkenHover' }));
const darkenClick = (args) => modifyColor(Object.assign(Object.assign({}, args), { func: darken, amount: 'darkenClick' }));
const transHover = (args) => modifyColor(Object.assign(Object.assign({}, args), { func: transparentize, amount: 'transparentizeHover' }));
const transClick = (args) => modifyColor(Object.assign(Object.assign({}, args), { func: transparentize, amount: 'transparentizeClick' }));
const colorVariant = props => {
    const theme = produce(props.theme, (draft) => {
        if (props.buttonColor) {
            draft.buttons.primary.borderColor = props.buttonColor;
            draft.buttons.primary.backgroundColor = props.buttonColor;
            draft.buttons.primary[':hover'].borderColor = props.buttonColor;
            draft.buttons.primary[':hover'].backgroundColor = props.buttonColor;
            draft.buttons.primary[':active'].borderColor = props.buttonColor;
            draft.buttons.primary[':active'].backgroundColor = props.buttonColor;
            draft.buttons.secondary.color = props.buttonColor;
            draft.buttons.secondary.borderColor = props.buttonColor;
            draft.buttons.secondary[':hover'].borderColor = props.buttonColor;
            draft.buttons.secondary[':hover'].backgroundColor = props.buttonColor;
            draft.buttons.secondary[':active'].borderColor = props.buttonColor;
            draft.buttons.secondary[':active'].backgroundColor = props.buttonColor;
            draft.buttons.tertiary.color = props.buttonColor;
            draft.buttons.tertiary[':hover'].color = props.buttonColor;
            draft.buttons.tertiary[':hover'].borderColor = props.buttonColor;
            draft.buttons.tertiary[':hover'].backgroundColor = props.buttonColor;
            draft.buttons.tertiary[':active'].color = props.buttonColor;
            draft.buttons.tertiary[':active'].borderColor = props.buttonColor;
            draft.buttons.tertiary[':active'].backgroundColor = props.buttonColor;
        }
        draft.buttons.primary[':hover'] = darkenHover({
            theme: draft,
            styles: draft.buttons.primary[':hover'],
            properties: ['borderColor', 'backgroundColor']
        });
        draft.buttons.primary[':active'] = darkenClick({
            theme: draft,
            styles: draft.buttons.primary[':active'],
            properties: ['borderColor', 'backgroundColor']
        });
        draft.buttons.secondary[':hover'] = darkenHover({
            theme: draft,
            styles: draft.buttons.secondary[':hover'],
            properties: ['borderColor', 'backgroundColor']
        });
        draft.buttons.secondary[':active'] = darkenClick({
            theme: draft,
            styles: draft.buttons.secondary[':active'],
            properties: ['borderColor', 'backgroundColor']
        });
        draft.buttons.tertiary[':hover'] = transHover({
            theme: draft,
            styles: draft.buttons.tertiary[':hover'],
            properties: ['borderColor', 'backgroundColor']
        });
        draft.buttons.tertiary[':active'] = transClick({
            theme: draft,
            styles: draft.buttons.tertiary[':active'],
            properties: ['borderColor', 'backgroundColor']
        });
    });
    return variant({ scale: 'buttons', variants: props.theme.buttons })(Object.assign(Object.assign({}, props), { theme }));
};
const Button = styled.button `
  ${button}
  ${sizeVariant}
  ${colorVariant}
  ${compose(typography, space, color, border, alignSelf)}
`;
Button.defaultProps = {
    theme,
    variant: 'secondary',
    size: 'medium'
};
export default Button;
//# sourceMappingURL=Button.js.map